// RecipientAutocompleteField — chip-style To/Cc/Bcc input with
// autocomplete from /api/contacts/email-suggestions.
//
// Outlook redesign Phase 3. Designed to slot into both the existing
// EmailComposer drawer AND the new InlineReplyComposer (rendered
// inside the reading pane). Same component everywhere; the parent
// owns state.
//
// Behavior:
//   * Renders the `value` (string[]) as chips, plus a free-text
//     input on the trailing edge.
//   * Typing in the input fires /api/contacts/email-suggestions
//     after 200ms debounce + min 2 chars; results render in a
//     dropdown directly below the input.
//   * Click a suggestion → adds its email as a chip.
//   * Press Enter / comma / Tab → confirms the highlighted
//     suggestion (or the typed text as a free-form chip if no
//     suggestion is highlighted).
//   * Press Backspace on an empty input → removes the last chip.
//   * Click an "x" on a chip → removes it.
//
// Suggestion display:
//   * "Mary Smith — mary@example.com" with the household label
//     dimmed underneath ("Smith Family").
//   * Up to 8 results.
//
// Accessibility:
//   * The input has role=combobox, aria-expanded, aria-controls
//     pointing at the listbox.
//   * Listbox items use role=option with aria-selected.

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import { api, ApiError, type EmailRecipientSuggestion } from "../../api";

export interface RecipientAutocompleteFieldProps {
  // The current chip values (one per recipient). Always emails or
  // raw text the user has confirmed (Enter / comma / Tab).
  value: string[];
  onChange: (next: string[]) => void;
  // The label shown to the left ("To" / "Cc" / "Bcc").
  label: string;
  placeholder?: string;
  // Whether to autofocus on mount. Used by the inline reply composer
  // to focus the To field as soon as the composer opens.
  autoFocus?: boolean;
  // Disable the field while a send is in flight.
  disabled?: boolean;
}

const DEBOUNCE_MS = 200;
// Suggestions begin at 3 typed characters (Grant's spec, 2026-08-13 —
// also the trigram search-index minimum server-side) and narrow as the
// user keeps typing.
const MIN_QUERY_CHARS = 3;
const MAX_SUGGESTIONS = 8;

function looksLikeEmail(s: string): boolean {
  return /\S+@\S+\.\S+/.test(s);
}

export function RecipientAutocompleteField({
  value,
  onChange,
  label,
  placeholder,
  autoFocus,
  disabled,
}: RecipientAutocompleteFieldProps) {
  const listboxId = useId();
  const [draft, setDraft] = useState("");
  // Audit D2: true after an attempted commit of non-email text.
  const [invalidDraft, setInvalidDraft] = useState(false);
  const [suggestions, setSuggestions] = useState<EmailRecipientSuggestion[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  // Grant's doctrine (2026-07-16, useSearchTypeahead): "never a silent
  // empty." When the suggestions endpoint fails, say so in plain words
  // instead of showing nothing — a silent empty is exactly how this
  // endpoint shipped dead (route-shadowed, 404 on every keystroke) and
  // nobody could tell a broken feature from a no-match.
  const [unavailable, setUnavailable] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Track the latest fetch so an out-of-order response doesn't
  // overwrite suggestions for a newer query.
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Debounced fetch on draft change.
  useEffect(() => {
    const trimmed = draft.trim();
    if (trimmed.length < MIN_QUERY_CHARS) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const seq = ++fetchSeqRef.current;
    // Abort the superseded request outright — a stale suggestions
    // query left running still costs database CPU server-side.
    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const resp = await api.getEmailSuggestions(
          trimmed,
          MAX_SUGGESTIONS,
          controller.signal,
        );
        if (fetchSeqRef.current !== seq) return; // stale
        setUnavailable(false);
        // Filter out suggestions whose email is already in value.
        const existing = new Set(value.map((v) => v.toLowerCase()));
        const filtered = resp.suggestions.filter(
          (s) => !existing.has(s.email.toLowerCase()),
        );
        setSuggestions(filtered);
        setHighlightIndex(0);
        setOpen(filtered.length > 0);
      } catch (err) {
        // An aborted request is a superseded keystroke, not a failure.
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Autocomplete failures are non-fatal — the user can still
        // type a free-form recipient — but they must be VISIBLE
        // (never a silent empty). 429 = rate limit, still worth
        // saying; anything else logs for diagnosis too.
        if (!(err instanceof ApiError) || err.status !== 429) {
          console.warn("recipient autocomplete failed", err);
        }
        if (fetchSeqRef.current === seq) {
          setSuggestions([]);
          setOpen(false);
          setUnavailable(true);
        }
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [draft, value]);

  const addChip = useCallback(
    (text: string) => {
      const trimmed = text.trim().replace(/[,;]+$/g, "");
      if (trimmed.length === 0) return;
      // Audit D2: only accept real email addresses as chips — bare
      // text like "bob" used to commit silently and the send failed
      // later with a confusing server error. Keep the draft in the
      // input so the user can fix it.
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setInvalidDraft(true);
        return;
      }
      setInvalidDraft(false);
      // Dedup case-insensitively.
      const lower = trimmed.toLowerCase();
      if (value.some((v) => v.toLowerCase() === lower)) {
        setDraft("");
        return;
      }
      onChange([...value, trimmed]);
      setDraft("");
      setSuggestions([]);
      setOpen(false);
    },
    [value, onChange],
  );

  const removeChipAt = useCallback(
    (index: number) => {
      if (index < 0 || index >= value.length) return;
      const next = [...value];
      next.splice(index, 1);
      onChange(next);
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        if (suggestions.length > 0) {
          e.preventDefault();
          setHighlightIndex((i) => (i + 1) % suggestions.length);
          setOpen(true);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        if (suggestions.length > 0) {
          e.preventDefault();
          setHighlightIndex(
            (i) => (i - 1 + suggestions.length) % suggestions.length,
          );
          setOpen(true);
        }
        return;
      }
      if (e.key === "Escape") {
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        return;
      }
      if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
        if (open && suggestions[highlightIndex]) {
          e.preventDefault();
          addChip(suggestions[highlightIndex]!.email);
          return;
        }
        if (draft.trim().length > 0) {
          e.preventDefault();
          addChip(draft);
          return;
        }
      }
      if (e.key === "Backspace" && draft.length === 0 && value.length > 0) {
        e.preventDefault();
        removeChipAt(value.length - 1);
      }
    },
    [draft, open, suggestions, highlightIndex, addChip, removeChipAt, value.length],
  );

  // When focus leaves the input, treat any non-empty draft as a chip
  // unless the user is clicking on a suggestion (we use mousedown
  // on the listbox to add before blur fires).
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      // Defer so a click on the listbox can take precedence.
      setOpen(false);
      if (draft.trim().length > 0) {
        addChip(draft);
      }
    }, 100);
  }, [draft, addChip]);

  return (
    <div
      className={`recipient-autocomplete${
        disabled ? " is-disabled" : ""
      }`}
    >
      <div className="recipient-autocomplete-label">{label}</div>
      <div
        className="recipient-autocomplete-field"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((chip, index) => (
          <span
            key={`${chip}-${index}`}
            className={`recipient-chip${
              looksLikeEmail(chip) ? "" : " recipient-chip-warn"
            }`}
            title={
              looksLikeEmail(chip)
                ? chip
                : `${chip} — doesn't look like a valid email`
            }
          >
            <span className="recipient-chip-text">{chip}</span>
            <button
              type="button"
              className="recipient-chip-remove"
              onClick={(e) => {
                e.stopPropagation();
                removeChipAt(index);
              }}
              aria-label={`Remove ${chip}`}
              tabIndex={-1}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="recipient-autocomplete-input"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (invalidDraft) setInvalidDraft(false);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onBlur={handleBlur}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          role="combobox"
          aria-invalid={invalidDraft || undefined}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
        />
      </div>
      {invalidDraft ? (
        <div className="recipient-autocomplete-invalid" role="alert">
          That doesn't look like an email address — check it and press
          Enter again.
        </div>
      ) : null}
      {unavailable && !invalidDraft ? (
        <div className="recipient-autocomplete-invalid" role="status">
          Suggestions aren't loading right now — you can still type a
          full email address and press Enter.
        </div>
      ) : null}
      {open && suggestions.length > 0 ? (
        <ul
          id={listboxId}
          role="listbox"
          className="recipient-autocomplete-listbox"
        >
          {suggestions.map((s, index) => {
            // A "Teammates" divider ahead of the first employee result —
            // contacts and teammates are two different kinds of person
            // to email, so they read as two groups, not one blended
            // list (Nate, 2026-08-13: "can I loop in Joey").
            const isFirstEmployee =
              s.kind === "employee" &&
              (index === 0 || suggestions[index - 1]!.kind !== "employee");
            return (
              <li key={`${s.kind}-${s.kind === "contact" ? s.contactId : s.userId}`} role="presentation">
                {isFirstEmployee ? (
                  <div className="recipient-autocomplete-group-label">
                    Teammates
                  </div>
                ) : null}
                <div
                  role="option"
                  aria-selected={index === highlightIndex}
                  className={`recipient-autocomplete-option${
                    index === highlightIndex ? " is-highlighted" : ""
                  }`}
                  // mousedown fires before blur, so we add the chip here
                  // and the input retains focus naturally.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addChip(s.email);
                  }}
                  onMouseEnter={() => setHighlightIndex(index)}
                >
                  <div className="recipient-autocomplete-option-primary">
                    <span className="recipient-autocomplete-option-name">
                      {s.kind === "contact" ? s.preferredName ?? s.name : s.name}
                    </span>
                    <span className="recipient-autocomplete-option-email">
                      {s.email}
                    </span>
                  </div>
                  <div className="recipient-autocomplete-option-secondary">
                    {s.kind === "contact" ? (
                      <>
                        <span className="recipient-autocomplete-option-household">
                          {s.householdLabel}
                        </span>
                        {s.companies.length > 0 ? (
                          <span className="recipient-autocomplete-option-companies">
                            {s.companies
                              .map(
                                (c) =>
                                  c.charAt(0).toUpperCase() +
                                  c.slice(1).toLowerCase(),
                              )
                              .join(" · ")}
                          </span>
                        ) : null}
                      </>
                    ) : s.roleLabel ? (
                      <span className="recipient-autocomplete-option-household">
                        {s.roleLabel}
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
